import { useState } from 'react'
import { Link } from 'react-router-dom'
import { sendPasswordResetEmail, getPortalUrls } from '@lipaflow/shared'
import { AuthShell, Button, Card, Input } from '@lipaflow/ui'
import { CheckCircleIcon, ArrowLeftIcon, KeyIcon } from '@heroicons/react/24/outline'

/**
 * Admin password reset. Previously a hand-rolled page with bespoke inputs,
 * buttons and an inline SVG logo; it now uses the shared AuthShell +
 * primitives so it matches the console sign-in exactly. Behaviour is
 * unchanged — the success state is shown regardless of whether the address
 * exists, so the form never reveals account existence.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const portalUrls = getPortalUrls()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await sendPasswordResetEmail(email)
      setIsSubmitted(true)
    } catch {
      // Don't reveal whether the email exists — always show success
      setIsSubmitted(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell
      compact
      brandSubtitle="Admin Console"
      cardFooter={
        <p className="text-center font-inter text-caption text-ink-faint">
          School or property staff?{' '}
          <a href={portalUrls.school} className="text-ink-muted underline hover:text-ink">
            School Portal
          </a>
          {' · '}
          <a href={portalUrls.property} className="text-ink-muted underline hover:text-ink">
            Property Portal
          </a>
        </p>
      }
    >
      <Card padding="lg" radius="panel">
        {isSubmitted ? (
          <>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-card bg-success-soft">
                <CheckCircleIcon className="h-6 w-6 text-success" aria-hidden="true" />
              </div>
              <h2 className="font-outfit text-h2 text-ink">Check your inbox</h2>
              <p className="mt-1.5 font-inter text-body-sm text-ink-muted">
                If a super admin account matches{' '}
                <strong className="font-semibold text-ink">{email}</strong>, a secure reset
                link has been sent.
              </p>
            </div>

            <p className="mt-5 text-center font-inter text-caption text-ink-faint">
              Links expire after 60 minutes for security compliance.
            </p>

            <Link to="/login" className="mt-6 block">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                leftIcon={<ArrowLeftIcon className="h-4 w-4" />}
              >
                Back to console login
              </Button>
            </Link>
          </>
        ) : (
          <>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-card bg-accent-soft">
                <KeyIcon className="h-6 w-6 text-accent" aria-hidden="true" />
              </div>
              <h2 className="font-outfit text-h2 text-ink">Reset admin password</h2>
              <p className="mt-1.5 font-inter text-body-sm text-ink-muted">
                Enter your registered super admin email and we&rsquo;ll send recovery
                instructions.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Email address"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="admin@lipaflow.co.ke"
                required
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={!email}
                isLoading={isSubmitting}
                loadingText="Sending…"
              >
                Send reset link
              </Button>
            </form>

            <div className="mt-6 border-t border-hairline pt-5 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 font-inter text-body-sm font-medium text-accent transition-colors hover:text-accent-hover"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </AuthShell>
  )
}
